/**
 * Configuración de i18next + react-i18next.
 *
 * Doc canónico: ../../../Docs/19-INTERNATIONALIZATION.md.
 *
 * Reglas:
 * - Idiomas soportados en MVP: 'es' (default) y 'en'.
 * - Detección inicial: localStorage → navigator → 'es'.
 *   Tras el primer login, la fuente de verdad pasa a `users.locale`
 *   (PATCH /me) — el switcher escribe ahí.
 * - Toda copy user-facing va por t('namespace.key'). Cero strings
 *   hardcodeados en JSX (Doc 19 §3.1).
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import esCommon from './locales/es/common.json'
import esAgents from './locales/es/agents.json'
import esAi from './locales/es/ai.json'
import esAuth from './locales/es/auth.json'
import esEditor from './locales/es/editor.json'
import esErrors from './locales/es/errors.json'
import esProjects from './locales/es/projects.json'
import esShell from './locales/es/shell.json'
import esSources from './locales/es/sources.json'
import esTeam from './locales/es/team.json'
import esPlatform from './locales/es/platform.json'
import esTemplates from './locales/es/templates.json'
import esUsage from './locales/es/usage.json'
import esWorkspace from './locales/es/workspace.json'
import enCommon from './locales/en/common.json'
import enAgents from './locales/en/agents.json'
import enAi from './locales/en/ai.json'
import enAuth from './locales/en/auth.json'
import enEditor from './locales/en/editor.json'
import enErrors from './locales/en/errors.json'
import enProjects from './locales/en/projects.json'
import enShell from './locales/en/shell.json'
import enSources from './locales/en/sources.json'
import enTeam from './locales/en/team.json'
import enPlatform from './locales/en/platform.json'
import enTemplates from './locales/en/templates.json'
import enUsage from './locales/en/usage.json'
import enWorkspace from './locales/en/workspace.json'

export const SUPPORTED_LOCALES = ['es', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: SupportedLocale = 'es'

/** Verifica si un valor arbitrario es un locale válido. */
export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  )
}

/** Orden en que se busca un contenido por idioma: el pedido, luego el
 * resto de `SUPPORTED_LOCALES` (el default primero). Espejo de
 * `pick_localized` del backend (`app/core/i18n.py`). */
export function localeFallbackOrder(locale: string): string[] {
  return [locale, ...SUPPORTED_LOCALES.filter((l) => l !== locale)]
}

/** De un contenido por idioma (`{ es: 'Revisión', en: 'Review' }`), el
 * texto en `locale` o, si falta, en el siguiente de `localeFallbackOrder`.
 * `undefined` si no hay ninguno. */
export function pickLocalized(
  values: Partial<Record<string, string | undefined>> | null | undefined,
  locale: string,
): string | undefined {
  if (!values) return undefined
  for (const candidate of localeFallbackOrder(locale)) {
    const value = values[candidate]?.trim()
    if (value) return value
  }
  return undefined
}

/** Idioma en que se edita un contenido por idioma (plantilla, metodología):
 * `preferred` (el de la interfaz, o el que ya se estaba editando) si el
 * contenido lo tiene; si no, el primero que tenga en el orden de
 * `SUPPORTED_LOCALES`. Nunca uno que el contenido no declare, salvo que no
 * declare ninguno. Agregar un idioma es una acción explícita ("+ idioma"). */
export function editingLocale(
  declared: Iterable<string>,
  preferred: string | null | undefined,
): SupportedLocale {
  const available = new Set(declared)
  const wanted = normalizeLocale(preferred)
  if (available.has(wanted)) return wanted
  return SUPPORTED_LOCALES.find((loc) => available.has(loc)) ?? wanted
}

/** Normaliza cualquier locale crudo (header navegador, valor de BD, etc.) a
 * uno de los soportados. Fallback al default. */
export function normalizeLocale(raw: string | null | undefined): SupportedLocale {
  if (!raw) return DEFAULT_LOCALE
  const lower = raw.trim().toLowerCase()
  if (isSupportedLocale(lower)) return lower
  // 'en-US,en;q=0.9' o 'es-MX' → primer match de 2 letras
  const match = /^([a-z]{2})/.exec(lower)
  if (match && isSupportedLocale(match[1])) return match[1]
  return DEFAULT_LOCALE
}

/** Mapeo `SupportedLocale` → región default para `Intl.*` (NumberFormat,
 * DateTimeFormat). Necesario porque `Intl.NumberFormat('es')` se resuelve
 * como `es-ES` (España: punto como separador de miles, coma decimal) —
 * formato que confunde a usuarios de México/LatAm/USA acostumbrados a
 * coma para miles y punto para decimales. HIVA Nexus target primario es
 * México/LatAm; por eso defaults a `es-MX` para `'es'` y `en-US` para `'en'`.
 *
 * **TODO multi-región** (cuando aparezca el primer cliente europeo):
 * el plan completo de migración a `tenant.preferred_region` está en
 * `Docs/19-INTERNATIONALIZATION.md §9`. Resumen: ~3-4h end-to-end
 * (migración Alembic + API + helper + Settings UI + tests). Mientras
 * tanto, asumir MX/US es la decisión consciente (no un parche).
 */
const REGION_BY_LOCALE: Record<SupportedLocale, string> = {
  es: 'es-MX',
  en: 'en-US',
}

/** Convierte un locale crudo (puede traer región o no) en uno con
 * región listo para pasar a `Intl.NumberFormat` / `Intl.DateTimeFormat`.
 *
 *  - `'es'` → `'es-MX'`
 *  - `'en'` → `'en-US'`
 *  - `'es-AR'` → `'es-AR'` (respeta región explícita, asume válida)
 *  - `'en-GB'` → `'en-GB'`
 *  - `undefined` / `''` / locale no soportado → `'es-MX'` (default)
 *
 * **Cuándo usarlo:** TODO call site que invoca `Intl.NumberFormat`,
 * `Intl.DateTimeFormat` o `toLocaleString` familia. NUNCA pasar `'es'`
 * o `'en'` directo a esos APIs — produce formato europeo en `'es'`.
 *
 * **Cuándo NO usarlo:** `i18n.changeLanguage()`, `t()` namespaces,
 * lookups por idioma en plantilla (`i18n.{es,en}.*`) — esos siguen
 * siendo el código 2-letter sin región.
 */
export function toIntlLocale(raw: string | null | undefined): string {
  if (!raw) return REGION_BY_LOCALE[DEFAULT_LOCALE]
  const trimmed = raw.trim()
  // Locale con región explícita (`es-MX`, `en-GB`, `es-AR`, `pt-BR`...)
  // se respeta tal cual. Validación BCP-47 mínima: 2 letras + guion +
  // al menos 2 alfanuméricos.
  if (/^[a-z]{2}-[a-z0-9]{2,}$/i.test(trimmed)) return trimmed
  // Locale 2-letter (`es`, `en`) → mapeamos a su región default.
  const lower = trimmed.toLowerCase()
  if (isSupportedLocale(lower)) return REGION_BY_LOCALE[lower]
  // Cadena rara tipo `en-US,en;q=0.9` o `fr`: normalizamos via
  // `normalizeLocale` y luego mapeamos.
  return REGION_BY_LOCALE[normalizeLocale(trimmed)]
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    // Si el detector entrega 'fr', i18next degrada a fallbackLng en runtime.
    nonExplicitSupportedLngs: false,
    defaultNS: 'common',
    ns: [
      'common',
      'agents',
      'ai',
      'auth',
      'editor',
      'errors',
      'projects',
      'shell',
      'sources',
      'team',
      'templates',
      'workspace',
    ],
    interpolation: {
      // React ya escapa por default; no hace falta el escape de i18next.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'hivanexus.locale',
      caches: ['localStorage'],
    },
    resources: {
      es: {
        common: esCommon,
        agents: esAgents,
        ai: esAi,
        auth: esAuth,
        editor: esEditor,
        errors: esErrors,
        projects: esProjects,
        shell: esShell,
        sources: esSources,
        team: esTeam,
        platform: esPlatform,
        templates: esTemplates,
        usage: esUsage,
        workspace: esWorkspace,
      },
      en: {
        common: enCommon,
        agents: enAgents,
        ai: enAi,
        auth: enAuth,
        editor: enEditor,
        errors: enErrors,
        projects: enProjects,
        shell: enShell,
        sources: enSources,
        team: enTeam,
        platform: enPlatform,
        templates: enTemplates,
        usage: enUsage,
        workspace: enWorkspace,
      },
    },
  })

/** Cambia el idioma activo en runtime. Persiste en localStorage por el
 * detector. El switcher de UI también persiste a `users.locale` vía
 * PATCH /me — esa es la fuente de verdad post-auth. */
export async function changeLocale(locale: SupportedLocale): Promise<void> {
  await i18n.changeLanguage(locale)
}

export default i18n
